import React from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { X, Plus, Minus, ShoppingCart, Trash2, ArrowRight, Package, Truck } from 'lucide-react';
import { categories, FREE_SHIPPING_THRESHOLD } from '../../data/mock';

// Placeholder Image Component
const PlaceholderImage = ({ className = '' }) => (
  <div className={`bg-neutral-700 flex items-center justify-center ${className}`}>
    <Package className="w-6 h-6 text-neutral-500" />
  </div>
);

const CartDrawer = () => {
  const { 
    isCartOpen, 
    closeCart, 
    cartItems, 
    cartTotal, 
    updateQuantity, 
    removeFromCart 
  } = useCart();

  if (!isCartOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-neutral-950/80 backdrop-blur-sm z-50"
        onClick={closeCart}
      />
      
      {/* Drawer */}
      <div className="fixed top-0 right-0 h-full w-full max-w-md bg-neutral-900 z-50 shadow-2xl flex flex-col border-l border-neutral-800">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-neutral-800 bg-gradient-to-r from-axon-700 to-axon-600">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-white/20 rounded-xl flex items-center justify-center">
              <ShoppingCart className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-display text-lg text-white uppercase tracking-wide">Your Cart</h2>
              <p className="text-sm text-axon-200">{cartItems.length} {cartItems.length === 1 ? 'item' : 'items'}</p>
            </div>
          </div>
          <button 
            onClick={closeCart}
            className="p-2.5 hover:bg-white/10 rounded-xl transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-5 bg-neutral-950">
          {cartItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-24 h-24 bg-neutral-800 rounded-2xl flex items-center justify-center mb-5">
                <Package className="w-10 h-10 text-neutral-600" />
              </div>
              <h3 className="font-display font-bold text-white text-lg uppercase tracking-wide mb-2">Your cart is empty</h3>
              <p className="text-neutral-500 text-sm mb-6">Start shopping for supplements</p>
              <button 
                onClick={closeCart}
                className="px-6 py-3 bg-gradient-to-r from-axon-600 to-axon-500 hover:from-axon-500 hover:to-axon-400 text-white font-bold uppercase tracking-wider rounded-xl transition-all shadow-button"
              >
                Browse Products
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {cartItems.map(item => {
                const itemCategory = categories.find(c => c.id === item.categoryId);
                return (
                  <div key={item.id} className="flex gap-4 p-4 bg-neutral-900 rounded-2xl border border-neutral-800">
                    {/* Image */}
                    <div className="w-20 h-20 bg-neutral-800 rounded-xl overflow-hidden flex-shrink-0">
                      {item.image ? (
                        <img 
                          src={item.image} 
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <PlaceholderImage className="w-full h-full" />
                      )}
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-axon-500 uppercase tracking-wider font-semibold mb-1">
                        {itemCategory?.name || 'Supplements'}
                      </p>
                      <h4 className="font-medium text-white text-sm mb-1.5 line-clamp-2">
                        {item.name}
                      </h4>
                      <p className="text-white font-bold">
                        ${item.price.toFixed(2)}
                      </p>
                      
                      {/* Quantity Controls */}
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center border border-neutral-700 rounded-xl bg-neutral-800 overflow-hidden">
                          <button 
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            className="p-2 hover:bg-neutral-700 transition-colors"
                          >
                            <Minus className="w-3 h-3 text-neutral-400" />
                          </button>
                          <span className="w-8 text-center text-sm font-semibold text-white">
                            {item.quantity}
                          </span>
                          <button 
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="p-2 hover:bg-neutral-700 transition-colors"
                          >
                            <Plus className="w-3 h-3 text-neutral-400" />
                          </button>
                        </div>
                        <button 
                          onClick={() => removeFromCart(item.id)}
                          className="p-2 text-neutral-500 hover:text-red-500 hover:bg-neutral-800 rounded-xl transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {cartItems.length > 0 && (
          <div className="p-5 border-t border-neutral-800 bg-neutral-900 space-y-4">
            {/* Free Shipping Progress */}
            {cartTotal < FREE_SHIPPING_THRESHOLD && (
              <div className="bg-neutral-800 rounded-xl p-4">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-neutral-400 flex items-center gap-2">
                    <Truck className="w-4 h-4" />
                    Add ${(FREE_SHIPPING_THRESHOLD - cartTotal).toFixed(2)} more for FREE shipping
                  </span>
                </div>
                <div className="w-full bg-neutral-700 rounded-full h-2 overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-axon-600 to-axon-500 rounded-full transition-all duration-300"
                    style={{ width: `${Math.min((cartTotal / FREE_SHIPPING_THRESHOLD) * 100, 100)}%` }}
                  />
                </div>
              </div>
            )}
            {cartTotal >= FREE_SHIPPING_THRESHOLD && (
              <div className="flex items-center gap-2 px-4 py-3 bg-axon-600/20 border border-axon-500/30 rounded-xl">
                <Truck className="w-5 h-5 text-axon-500" />
                <span className="text-axon-400 text-sm font-medium">You've unlocked FREE shipping!</span>
              </div>
            )}

            {/* Subtotal */}
            <div className="flex items-center justify-between">
              <span className="text-neutral-400 font-medium">Subtotal</span>
              <span className="text-2xl font-bold text-white">${cartTotal.toFixed(2)}</span>
            </div>

            {/* Checkout Button */}
            <Link
              to="/checkout"
              onClick={closeCart}
              className="flex items-center justify-center gap-2 w-full py-4 bg-gradient-to-r from-axon-600 to-axon-500 hover:from-axon-500 hover:to-axon-400 text-white font-bold uppercase tracking-wider rounded-xl transition-all shadow-button hover:shadow-glow-cyan"
            >
              Checkout
              <ArrowRight className="w-5 h-5" />
            </Link>

            {/* Continue Shopping */}
            <button 
              onClick={closeCart}
              className="w-full text-center text-neutral-400 hover:text-white py-2 text-sm font-medium transition-colors"
            >
              Continue Shopping
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default CartDrawer;
